import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import DynamicIcon from "../DynamicIcon";
import {
    getProductCategoriesTree,
    selectProductCategoriesTree,
    selectProductCategoriesTreeLoading
} from "../../redux/productCategory/productCategorySlice";
import { selectAccountData } from "../../redux/main/accountSlice";

function TreeNode({ category, level = 0, selectedCategories, onToggle, isLast = false }) {
    const [isExpanded, setIsExpanded] = useState(level < 2);
    const hasChildren = category.children && category.children.length > 0;
    const isSelected = selectedCategories.some(c => c.categoryId === category._id);

    const handleCheck = (e) => {
        e.stopPropagation();
        onToggle({
            categoryId: category._id,
            categoryName: category.name
        });
    };

    return (
        <div>
            <div
                className={`flex items-center py-1.5 px-2 hover:bg-gray-50 rounded transition-colors ${isSelected ? 'bg-blue-50' : ''}`}
            >
                {/* Indentazione con connettori */}
                {level > 0 && (
                    <div className="flex items-center flex-shrink-0" style={{ width: `${level * 24}px` }}>
                        {Array.from({ length: level }).map((_, i) => (
                            <div key={i} className="flex items-center justify-center" style={{ width: '24px' }}>
                                {i === level - 1 ? (
                                    <span className="text-gray-300 text-xs font-mono">
                                        {isLast ? '\u2514' : '\u251C'}
                                    </span>
                                ) : (
                                    <span className="text-gray-200 text-xs font-mono">{'\u2502'}</span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {/* Freccia espandi/comprimi */}
                {hasChildren ? (
                    <button
                        type="button"
                        className="p-0.5 hover:bg-gray-200 rounded text-gray-500 flex-shrink-0 mr-1"
                        onClick={(e) => {
                            e.stopPropagation();
                            setIsExpanded(!isExpanded);
                        }}
                    >
                        <DynamicIcon iconClass={`fa-solid fa-chevron-${isExpanded ? 'down' : 'right'} text-xs`} />
                    </button>
                ) : (
                    <span className="w-5 flex-shrink-0 mr-1" />
                )}

                {/* Checkbox */}
                <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={handleCheck}
                    className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 mr-2 flex-shrink-0 cursor-pointer"
                />

                {/* Icona e nome */}
                <DynamicIcon iconClass={`fa-solid ${level === 0 ? 'fa-folder text-yellow-500' : 'fa-folder-open text-yellow-400'} text-sm mr-1.5`} />
                <span
                    className={`text-sm cursor-pointer select-none ${isSelected ? 'font-medium text-blue-700' : 'text-gray-700'}`}
                    onClick={handleCheck}
                >
                    {category.name}
                </span>

                {/* Badge figli */}
                {hasChildren && (
                    <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded-full">
                        {category.children.length}
                    </span>
                )}
            </div>

            {/* Figli ricorsivi */}
            {isExpanded && hasChildren && (
                <div>
                    {category.children.map((child, index) => (
                        <TreeNode
                            key={child._id}
                            category={child}
                            level={level + 1}
                            selectedCategories={selectedCategories}
                            onToggle={onToggle}
                            isLast={index === category.children.length - 1}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}

export default function CategoryTreeSelect({ selectedCategories = [], onChange }) {
    const dispatch = useDispatch();
    const accountData = useSelector(selectAccountData);
    const categoriesTree = useSelector(selectProductCategoriesTree);
    const loading = useSelector(selectProductCategoriesTreeLoading);
    const token = localStorage.getItem("installamelo-auth-token") ?? "";

    useEffect(() => {
        if (accountData?._id && (!categoriesTree || categoriesTree.length === 0)) {
            dispatch(getProductCategoriesTree({ accountId: accountData._id, token }));
        }
    }, [accountData, dispatch, token]);

    const handleToggle = (category) => {
        const exists = selectedCategories.some(c => c.categoryId === category.categoryId);
        if (exists) {
            onChange(selectedCategories.filter(c => c.categoryId !== category.categoryId));
        } else {
            onChange([...selectedCategories, category]);
        }
    };

    if (loading) {
        return (
            <div className="text-sm text-gray-400 py-2">
                <DynamicIcon iconClass="fa-solid fa-spinner fa-spin mr-2" />
                Caricamento categorie...
            </div>
        );
    }

    if (!categoriesTree || categoriesTree.length === 0) {
        return (
            <div className="text-sm text-gray-400 py-2">
                Nessuna categoria disponibile.
            </div>
        );
    }

    return (
        <div className="border border-gray-200 rounded-lg p-2 max-h-64 overflow-y-auto bg-white">
            {categoriesTree.map((category) => (
                <TreeNode
                    key={category._id}
                    category={category}
                    selectedCategories={selectedCategories}
                    onToggle={handleToggle}
                />
            ))}
        </div>
    );
}
